/**
 * Sticky lead summary panel for desktop HITL wizard.
 */

import React from "react";
import {
  Box,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import ConfirmationChecklist from "./ConfirmationChecklist.jsx";

/**
 * @param {{
 *   fields: object,
 *   draft: object|null,
 *   checklistItems?: Array,
 *   onConfirmField?: (field: string) => void,
 *   onNavigateChecklist?: (item: object) => void,
 *   children?: React.ReactNode,
 * }} props
 */
const LeadSummaryPanel = ({
  fields = {},
  draft = null,
  checklistItems = [],
  onConfirmField,
  onNavigateChecklist,
  children,
}) => {
  const name = String(fields.name || "").trim() || "—";
  const phone = String(fields.phone || "").trim() || "—";
  const from = String(fields.moving_from || "").trim() || "—";
  const to = String(fields.moving_to || "").trim() || "—";
  const moveDate = String(fields.move_date || "").trim() || "—";
  const structuredCount = Array.isArray(fields.inventoryRows)
    ? fields.inventoryRows.filter((r) => String(r?.name || "").trim()).length
    : 0;
  const itemLines = String(fields.itemsText || "")
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const itemCount = structuredCount || itemLines.length;
  const confidencePct =
    draft?.confidence != null
      ? Math.round(Number(draft.confidence) * 100)
      : null;
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "?";

  return (
    <Box
      data-testid="lead-summary-panel"
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Lead summary
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: "0.85rem",
              flexShrink: 0,
            }}
          >
            {initials}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {phone}
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={0.75} sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            From
          </Typography>
          <Typography variant="body2">{from}</Typography>
          <Typography variant="caption" color="text.secondary">
            To
          </Typography>
          <Typography variant="body2">{to}</Typography>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Chip size="small" label={`Date: ${moveDate}`} variant="outlined" />
          {fields.distance_km ? (
            <Chip
              size="small"
              label={`${fields.distance_km} km`}
              variant="outlined"
            />
          ) : null}
          <Chip
            size="small"
            label={`${itemCount} item${itemCount === 1 ? "" : "s"}`}
            variant="outlined"
          />
          {fields.serviceType ? (
            <Chip
              size="small"
              color="primary"
              label={
                fields.serviceType === "shared_load"
                  ? "Share Load"
                  : fields.serviceType === "exclusive_load"
                    ? "Exclusive"
                    : fields.serviceType
              }
            />
          ) : null}
        </Stack>

        {confidencePct != null ? (
          <Box sx={{ mt: 1 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">
                AI confidence
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {confidencePct}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(0, confidencePct))}
              sx={{ mt: 0.5, height: 6, borderRadius: 1 }}
            />
          </Box>
        ) : null}
      </Box>

      <Divider />
      <ConfirmationChecklist
        items={checklistItems}
        onConfirmField={onConfirmField}
        onNavigate={onNavigateChecklist}
      />
      {children}
    </Box>
  );
};

export default LeadSummaryPanel;
